// src/components/OpenCASCADEGenerator/OpenCASCADEGenerator.js

import React, { useState, Suspense } from 'react';
import { generateModel, generateIterativeModel } from '../../services/apiService'; 
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import ModelViewer from '../Stepfile';


const OpenCASCADEGenerator = () => {
  // Single-shot states
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [stepFiles, setStepFiles] = useState([]);
  const [gltfFiles, setGltfFiles] = useState([]);

  // ----- ADDED FOR OPTION 4 (Modal/Iterative) -----
  const [showRefineModal, setShowRefineModal] = useState(false);
  const [messages, setMessages] = useState([]); // Chat conversation array
  const [userPrompt, setUserPrompt] = useState('');
  const [iterStepFiles, setIterStepFiles] = useState([]);
  const [iterGltfFiles, setIterGltfFiles] = useState([]);
  // -------------------------------------------------

  const examples = [
    {
      title: "Design a cup",
      description: "Create a cup."
    },
    {
      title: "Design a car",
      description: "Design a simple car geometry for mars."
    },
    {
      title: "Cylindrical Tank",
      description: "Create a cylindrical tank with a height of 20 mm and diameter of 10 mm..."
    },
    {
      title: "Building with Skylight",
      description: "Design a rectangle representing a building: 5 m long, 2 m wide..."
    },
    {
      title: "Sprinkler System",
      description: "Generate a sprinkler system for a 200 m x 200 m warehouse..."
    }
  ];

  // SINGLE-SHOT SUBMIT
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setResult('');
    setStepFiles([]);
    setGltfFiles([]);

    try {
      const data = await generateModel(description);
      setResult(data.code || '');
      setStepFiles(data.stepFiles || []);
      setGltfFiles(data.gltfFiles || []);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  // REFINE: open the modal, create initial conversation array
  const handleOpenRefineModal = () => {
    // Build conversation with system + user + assistant
    const initialMsgs = [
      { role: 'system', content: 'You are an AI assistant for generating 3D shapes.' },
      { role: 'user', content: description },
      { role: 'assistant', content: result },
    ];
    setMessages(initialMsgs);
    setIterStepFiles([]); // or set them to stepFiles if you want to carry them over
    setIterGltfFiles([]);
    setShowRefineModal(true);
  };

  // ITERATIVE SUBMIT INSIDE THE MODAL
  const handleIterativeSubmit = async () => {
    if (!userPrompt.trim()) return;
    const updated = [...messages, { role: 'user', content: userPrompt }];
    setMessages(updated);
    setUserPrompt('');

    try {
      // Call the iterative endpoint
      const data = await generateIterativeModel(updated);
      const final = [...updated, { role: 'assistant', content: data.code }];
      setMessages(final);

      // If you want to store new geometry from iterative
      setIterStepFiles(data.stepFiles || []);
      setIterGltfFiles(data.gltfFiles || []);
    } catch (err) {
      console.error(err);
    }
  };

  // RENDER SINGLE-SHOT STEP FILES
  // const renderStepFiles = () => {
  //   return stepFiles.map((file, index) => (
  //     <div key={index} className="my-2">
  //       <a
  //         href={file.url}
  //         download={file.name}
  //         className="inline-block py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
  //       >
  //         Download {file.name}
  //       </a>
  //       <a
  //         href={`https:/www.emachineshop.com/free-online-step-file-viewer`}
  //         target="_blank"
  //         rel="noopener noreferrer"
  //         className="inline-block py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-700 transition-colors"
  //         aria-label={`View ${file.name} in STEP file viewer`}
  //       >
  //         View Price to Build
  //       </a>
  //     </div>
  //   ));
  // };

  // RENDER SINGLE-SHOT STEP FILES
  // RENDER SINGLE-SHOT STEP FILES
  const renderStepFiles = () => {
    return (
      <div className="flex flex-col gap-4 w-full">
        {stepFiles.map((file, index) => (
          <div 
            key={index} 
            className="flex items-start gap-4 border p-4 rounded-lg shadow-md"
          >
            
            {/* ✅ Left: STEP File Download Button */}
            <a
              href={file.url}
              download={file.name}
              className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Download {file.name}
            </a>
  
            {/* ✅ Right: Model Viewer & External Link in a Column */}
            <div className="flex flex-col gap-2 flex-1">
              {/* ✅ Model Viewer */}
              <div className="border p-4 rounded-md bg-gray-100">
                <ModelViewer file={file} />
              </div>
  
              {/* ✅ External STEP File Viewer Link */}
              <a
                href="https://www.emachineshop.com/free-online-step-file-viewer"
                target="_blank"
                rel="noopener noreferrer"
                className="py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-700 transition-colors text-center"
                aria-label={`View ${file.name} in STEP file viewer`}
              >
                View Price to Build
              </a>
            </div>
  
          </div>
        ))}
      </div>
    );
  };
  

 

  // RENDER SINGLE-SHOT GLTF
  const renderGltfFiles = () => {
    return gltfFiles.map((file, index) => (
      <div key={index} className="my-4">
        <h4 className="font-medium mb-2 text-gray-900">3D Model {index + 1}</h4>
        <Canvas style={{ width: '100%', height: '400px' }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={1} />
          <Suspense fallback={<span>Loading model...</span>}>
            <ModelViewer modelUrl={file.url} />
          </Suspense>
          <OrbitControls />
        </Canvas>
      </div>
    ));
  };

  // RENDER ITERATIVE STEP FILES (INSIDE THE MODAL, IF YOU LIKE)
  const renderIterStepFiles = () => {
    return iterStepFiles.map((file, idx) => (
      <div key={idx} className="my-2">
        <a href={file.url} download={file.name}>
          Download {file.name}
        </a>
      </div>
    ));
  };

  // RENDER ITERATIVE GLTF (INSIDE THE MODAL)
  const renderIterGltfFiles = () => {
    return iterGltfFiles.map((file, idx) => (
      <div key={idx} className="my-4">
        <h4 className="font-medium mb-2 text-gray-900">Refined 3D Model {idx + 1}</h4>
        <Canvas style={{ width: '100%', height: '300px' }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={1} />
          <Suspense fallback={<span>Loading model...</span>}>
            <ModelViewer modelUrl={file.url} />
          </Suspense>
          <OrbitControls />
        </Canvas>
      </div>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome to Designgine</h1>
        <h2 className="text-lg font-medium text-gray-500">
          Bring your designs to life, view, edit and 3D print or CNC machine them
        </h2>
      </div>

      <div className="space-y-6">
        {/* Instructions Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Instructions</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Be specific about the shape(s) you want, including dimensions.</li>
            <li>Describe features (shape, location, size).</li>
            <li>If multiple parts, explain how they fit together.</li>
            <li>Include relevant standards if needed.</li>
          </ul>
        </div>

        {/* Examples Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Examples</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {examples.map((example, index) => (
              <div
                key={index}
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setDescription(example.description)}
              >
                <h4 className="font-medium mb-2 text-gray-900">{example.title}</h4>
                <p className="text-sm text-gray-600 line-clamp-3">{example.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Description Input and Submit Section */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Describe your desired 3D model
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Simply describe your design ideas here..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleSubmit}
            disabled={!description || loading}
            className={`w-full py-2 px-4 rounded-lg text-white font-medium 
              ${!description || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition-colors'}`}
          >
            {loading ? 'Generating...' : 'Generate Design'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="space-y-2">
            {/* <h3 className="text-lg font-medium text-gray-900">Generated Code</h3>
            <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{result}</code>
            </pre> */}

            {/* ADD A "REFINE DESIGN" BUTTON */}
            <button
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              onClick={handleOpenRefineModal}
            >
              Edit Design
            </button>
          </div>
        )}

        {/* STEP Files Section */}
        {stepFiles.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">Generated STEP Files</h3>
            {renderStepFiles()}
          </div>
        )}

        {/* GLTF Files Section */}
        {gltfFiles.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">Converted GLTF Files</h3>
            {renderGltfFiles()}
          </div>
        )}
      </div>

      {/* MODAL FOR ITERATIVE REFINEMENT */}
      {showRefineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-4 rounded shadow-lg max-w-2xl w-full relative">
            <button
              onClick={() => setShowRefineModal(false)}
              className="absolute top-2 right-2 text-gray-600"
            >
              X
            </button>

            <h2 className="text-xl font-bold mb-2">Refine Your Design</h2>

            {/* Show the conversation so far */}
            <div className="border h-48 overflow-y-auto p-2 mb-2">
              {messages.map((m, i) => (
                <div key={i} className="mb-1">
                  <strong>{m.role}:</strong> {m.content}
                </div>
              ))}
            </div>

            {/* Refinement textarea */}
            <textarea
              rows={3}
              className="w-full p-2 border"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Describe your refinement instructions..."
            />
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
              onClick={handleIterativeSubmit}
            >
              Refine
            </button>

            {/* Optionally show iterative step/gltf here if you want */}
            {iterStepFiles.length > 0 && (
              <div className="mt-4">
                <h4>Refined STEP</h4>
                {renderIterStepFiles()}
              </div>
            )}
            {iterGltfFiles.length > 0 && (
              <div className="mt-4">
                <h4>Refined GLTF</h4>
                {renderIterGltfFiles()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenCASCADEGenerator;
